// Removed unused imports and helper functions
import { useVehiclesStore } from '~/stores/vehicles';
import { useYouTubeStore } from '~/stores/youtubeStore';
import { useDataStore } from '~/layers/adme-blob-cms/store/dataStore'; // Import dataStore
import { defineNuxtRouteMiddleware, useSupabaseUser } from '#imports'; // Import useSupabaseUser
import type { RouteLocationNormalized } from 'vue-router';

export default defineNuxtRouteMiddleware(async (to: RouteLocationNormalized) => {
  // Only run this middleware on the server-side to pre-fetch data
  if (!process.server || isStaticAsset(to.path)) {
    return;
  }

  // Skip API and internal utility paths entirely
  const currentPath = to.path || ''
  if (
    currentPath.startsWith('/api') ||
    currentPath.startsWith('/_nuxt') ||
    currentPath.startsWith('/__') // devtools and internals
  ) {
    return;
  }

  // Check if Pinia is available before using stores
  try {
    const vehiclesStore = useVehiclesStore();
    const youtubeStore = useYouTubeStore();
    const dataStore = useDataStore(); // Instantiate dataStore
    const supabaseUser = useSupabaseUser(); // Get Supabase user

    console.log(`[SSR Middleware] Path: ${to.path}. Checking store initializations.`);

    try {
      // Ensure user data if authenticated (for sidebar links, etc.)
      if (supabaseUser.value && !dataStore.userData) {
        console.log(`[SSR Middleware] User authenticated, attempting to fetch user data for ${supabaseUser.value.email}.`);
        await dataStore.fetchUserData(supabaseUser.value.email);
        console.log(`[SSR Middleware] User data fetch attempt complete. UserDealershipIds count: ${dataStore.userDealershipIds?.length}`);
      } else if (supabaseUser.value && dataStore.userData && (!dataStore.userDealershipIds || dataStore.userDealershipIds.length === 0)) {
        console.log(`[SSR Middleware] User authenticated, userData present, but no dealership IDs. Re-fetching user data for ${supabaseUser.value.email}.`);
        await dataStore.fetchUserData(supabaseUser.value.email);
        console.log(`[SSR Middleware] User data re-fetch attempt complete. UserDealershipIds count: ${dataStore.userDealershipIds?.length}`);
      }

      // Ensure vehicles data (only on routes that actually need vehicles)
      if (shouldPrefetchVehiclesForRoute(currentPath)) {
        if (!vehiclesStore.isDataLoaded && !vehiclesStore.isLoading) {
          console.log(`[SSR Middleware] Vehicles data: NOT LOADED and NOT LOADING. Triggering fetchVehicles.`);
          await vehiclesStore.fetchVehicles();
          console.log(`[SSR Middleware] Vehicles store fetch complete. Data loaded: ${vehiclesStore.isDataLoaded}`);
        } else {
          console.log(`[SSR Middleware] Vehicles data: LOADED or ALREADY LOADING. (Loaded: ${vehiclesStore.isDataLoaded}, Loading: ${vehiclesStore.isLoading})`);
        }
      }

      // Ensure YouTube videos data (limit to routes that render the widget)
      if (shouldPrefetchYouTubeForRoute(currentPath)) {
        console.log(`[SSR Middleware] YouTube videos: Current count: ${youtubeStore.videos.length}, isLoading: ${youtubeStore.isLoading}`);
        if (youtubeStore.videos.length === 0 && !youtubeStore.isLoading) {
          console.log(`[SSR Middleware] YouTube videos: NOT LOADED and NOT LOADING. Triggering fetchVideos.`);
          await youtubeStore.fetchVideos();
          console.log(`[SSR Middleware] YouTube store fetch complete. Videos count: ${youtubeStore.videos.length}, Error: ${youtubeStore.error}`);
        } else {
          console.log(`[SSR Middleware] YouTube videos: LOADED or ALREADY LOADING.`);
        }
      }

    } catch (error) {
      console.error(`[SSR Middleware] CRITICAL ERROR during data fetch trigger:`, error);
      // Allow navigation to proceed even if data fetching fails
    }
  } catch (piniaError) {
    console.error(`[SSR Middleware] Pinia not available or store initialization failed:`, piniaError);
    // Allow navigation to proceed if Pinia is not available
    return;
  }
});

// Helper function to check if the path is for a static asset
function isStaticAsset(path: string): boolean {
  // Add common static file extensions
  return /\.(ico|png|jpg|jpeg|gif|svg|css|js|json|txt|woff|woff2|ttf|eot)(\?.*)?$/i.test(path);
}

// Helper: limit where vehicles prefetch runs
function shouldPrefetchVehiclesForRoute(pathname: string): boolean {
  // Pages that list or depend on vehicles
  return (
    pathname.startsWith('/cars-for-sale') ||
    pathname.startsWith('/vehicles') ||
    pathname.startsWith('/vehicle-for-sale') ||
    pathname.startsWith('/vms')
  )
}

// Helper: limit where YouTube prefetch runs (home, about, or pages known to include the widget)
function shouldPrefetchYouTubeForRoute(pathname: string): boolean {
  return (
    pathname === '/' ||
    pathname.startsWith('/about') ||
    pathname.startsWith('/vms')
  )
}
